// titleBarLogic.js — PURE, framework-free core for the Title Bar content block
// (Bucket D / Wave D2). Deck source of truth:
//   C:\iYuga\design-handoff\concepts\builder-blocks\15-titlebar-block-editor.html
//   (round-7 v3 — SUPERSEDES 14, which superseded 13; index.md file->screens row).
//
// A Title Bar is a standalone HEADING BAND: a plain rectangle spanning the
// Segment content width with a heading, a chosen text size / font / alignment,
// and a background REUSED from the Container block's #1996 picker
// (resolveContainerBackground). Deck render: "2px barely-rounded corners, no
// chevron, no pill, nothing expands/collapses" (state 14) — visually distinct
// from an accordion header.
//
// Every value-mapping decision (size->px, height->px, align->flex/text, and the
// AUTO text-contrast pick — deck: "titles auto-flip white/navy by background
// luminance, the Segment-icon rule") lives here so it is unit-tested without a
// DOM. The React view (TitleBarView.jsx) only turns these decisions into markup.
//
// Typography sizes come from the app design TOKENS (tokens.typography) — the same
// 12 / 16 / 24 scale the shared caption + 9-font component uses (deck: "size &
// font ride the same card that styles Title Bar blocks"). No magic type sizes.

import { typography, fontWeight } from "../../../../theme/tokens";

// ── Text size (S · M ✓ · L) — deck state 15 "Text size S·M✓·L·Custom → 16px".
// Mapped to the token scale so the block never re-hardcodes a font size.
export const TEXT_SIZES = ["s", "m", "l"];

export const sizeToPx = (size) => {
  switch (size) {
    case "s":
      return typography.text.fontSize; // 12
    case "l":
      return typography.largeTitle.fontSize; // 24
    case "m":
    default:
      return typography.subtitle.fontSize; // 16 (deck default M)
  }
};

// A heading band reads as a heading, so the title uses the token BOLD weight.
export const TITLE_WEIGHT = fontWeight.bold; // 700

// ── Bar height (S · M ✓ · L) — deck Size "Height S·M✓·L·Custom → 52px".
// Heights are component geometry (not part of the type scale), so they are
// named constants here, M = 52 per the deck.
export const BAR_HEIGHTS = ["s", "m", "l"];
export const HEIGHT_PX = { s: 40, m: 52, l: 72 };
export const heightToPx = (h) =>
  Object.prototype.hasOwnProperty.call(HEIGHT_PX, h) ? HEIGHT_PX[h] : HEIGHT_PX.m;

// ── Alignment (Left · Center ✓ · Right) — a heading band centers by default.
export const ALIGNMENTS = ["left", "center", "right"];
const JUSTIFY = { left: "flex-start", center: "center", right: "flex-end" };
export const alignToJustify = (a) => JUSTIFY[a] || JUSTIFY.center;
export const alignToTextAlign = (a) => (ALIGNMENTS.includes(a) ? a : "center");

// ── Auto text-contrast ────────────────────────────────────────────────────────
// Deck: "Titles auto-flip white/navy by background luminance (the Segment-icon
// rule) with soft scrims on media/pattern headers."
export const INK = "#123051"; // navy — tokens ink
export const LIGHT = "#FFFFFF";
// Soft scrim behind LIGHT text so it stays legible over an image/gradient or a
// mid-luminance solid (the deck's "soft scrims on media/pattern headers").
export const SCRIM_SHADOW = "0 1px 3px rgba(18,48,81,0.55)";

/**
 * WCAG relative luminance of a `#RGB` / `#RRGGBB` hex, or null when the value is
 * not a plain hex (a gradient / image CSS string we cannot sample — the caller
 * then falls back to a legible LIGHT + scrim default).
 * @param {string} hex
 * @returns {number|null} 0..1 luminance, or null
 */
export const relativeLuminance = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const toLin = (pair) => {
    const c = parseInt(pair, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const r = toLin(h.slice(0, 2));
  const g = toLin(h.slice(2, 4));
  const b = toLin(h.slice(4, 6));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// WCAG contrast ratio between two relative luminances.
const contrastRatio = (l1, l2) =>
  (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);

const INK_LUM = relativeLuminance(INK); // ~0.03 (computed once)

/**
 * Pick the heading color + text-shadow for a Title Bar.
 *
 *  - tone "light" -> white + scrim (author override)
 *  - tone "dark"  -> ink, no scrim (author override)
 *  - tone "auto" (default):
 *      • solid-color bar  -> whichever of ink / white has the HIGHER contrast
 *        against that color (maximise legibility, the Segment-icon rule).
 *      • structured fill  -> `solidHex` is null (an image/gradient we cannot
 *        sample) -> legible white + scrim by default (deck's media/pattern scrim).
 *
 * @param {"auto"|"light"|"dark"} tone
 * @param {string|null} solidHex the effective solid-color background, or null
 * @returns {{color:string, textShadow:string}}
 */
export const pickTextStyle = (tone, solidHex) => {
  if (tone === "light") return { color: LIGHT, textShadow: SCRIM_SHADOW };
  if (tone === "dark") return { color: INK, textShadow: "none" };
  const bgLum = relativeLuminance(solidHex);
  if (bgLum === null) return { color: LIGHT, textShadow: SCRIM_SHADOW };
  const cInk = contrastRatio(bgLum, INK_LUM);
  const cWhite = contrastRatio(bgLum, 1);
  return cInk >= cWhite
    ? { color: INK, textShadow: "none" }
    : { color: LIGHT, textShadow: SCRIM_SHADOW };
};

export const TEXT_TONES = ["auto", "light", "dark"];

// Deck: the bar itself is a plain rectangle with 2px "barely-rounded" corners.
export const BAR_CORNER_RADIUS = 2;

// The default solid bar color when no structured fill is chosen — a light purple
// tint (matches the Container picker's DEFAULT_COLOR) so AUTO text resolves to ink.
export const DEFAULT_BAR_COLOR = "#F3ECFF";

const titleBarLogic = {
  TEXT_SIZES,
  sizeToPx,
  TITLE_WEIGHT,
  BAR_HEIGHTS,
  HEIGHT_PX,
  heightToPx,
  ALIGNMENTS,
  alignToJustify,
  alignToTextAlign,
  relativeLuminance,
  pickTextStyle,
  TEXT_TONES,
  BAR_CORNER_RADIUS,
  DEFAULT_BAR_COLOR,
  INK,
  LIGHT,
  SCRIM_SHADOW,
};

export default titleBarLogic;
